<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { OneYearPointItemType } from '@/types/one-year'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import { treasuryInfoList } from '@/views/treasury/data'
import { onMounted } from 'vue'
import { ref } from 'vue'

const commonStore = useCommonStore()
const userStore = useUserStore()

const commodityInfoList = [{ name: '💎 钻石', desc: '', key: 'diamond' }, { name: '💰 金币', desc: '', key: 'gold' }, ...treasuryInfoList]

async function getPoint() {
  const res = await socketEmitAsync<number>({ type: 'getCelebrationPoint' })
  commonStore.celebrationPoint = res
}

const commodityList = ref<OneYearPointItemType[]>()
const curCommodity = ref<OneYearPointItemType>()
const buyCount = ref(1)
const curCommodityInfo = (key?: string) => {
  return commodityInfoList.find((info) => info.key === key)
}
async function getCrystalStoreCommodity() {
  const res = await socketEmitAsync<OneYearPointItemType[]>({ type: 'getOneYearPointMall' })
  commodityList.value = res
}
const buyCountMap = ref<[number, number][]>([])
async function getUserBuyCount() {
  const res = await socketEmitAsync<[number, number][]>({ type: 'getCelebrationPointMallBuyRecord' })
  buyCountMap.value = res
}
function handleBuyCommodity() {
  if (!curCommodity.value) return ElMessage.warning('未选中任何商品')
  const { id, key, price } = curCommodity.value
  const name = commodityInfoList.find((info) => info.key === key)?.name
  const totalPrice = price * buyCount.value
  if (commonStore.celebrationPoint < totalPrice) return ElMessage.warning('积分不足')
  const alreadyBuyCount = buyCountMap.value.find(([itemId]) => itemId === id)?.[1] || 0
  if (curCommodity.value.max && alreadyBuyCount + buyCount.value > curCommodity.value.max) return ElMessage.warning('已超出限购数量')
  ElMessageBox.confirm(`确认购买道具【${name}】吗？本次购买花费<b>${price * buyCount.value}</b>积分`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({
        type: 'buyCelebrationPointMallItem',
        data: { id, count: buyCount.value },
      })
      commonStore.celebrationPoint -= totalPrice
      const commodity = commodityList.value!.find((item) => item.id === id)
      if (!commodity) return

      switch (commodity.key) {
        case 'diamond': {
          if (!commodity.count) break
          userStore.diamond += commodity.count * buyCount.value
          break
        }
        case 'gold': {
          if (!commodity.count) break
          userStore.gold += commodity.count * buyCount.value
          break
        }
        default: {
          userStore.consumables[commodity.key] += buyCount.value * (commodity.count || 1)
        }
      }
      getUserBuyCount()
    })
    .catch(() => {})
}

onMounted(() => {
  getPoint()
  getCrystalStoreCommodity()
  getUserBuyCount()
})
</script>

<template>
  <p style="text-align: right; font-size: 12px; margin: 5px 0">
    <span>💲 {{ formatNumberWithCommas(commonStore.celebrationPoint) }}</span>
    <span style="margin-left: 8px">💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
    <span style="margin-left: 8px">💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
  </p>
  <div class="commodity-list-wrap border-wrap">
    <h4>活动积分商城</h4>
    <div class="commodity-list">
      <p style="width: 100%; flex-shrink: 0">高价值</p>
      <div class="item" v-for="item of commodityList?.slice(0, 8)" :key="item.id">
        <el-button
          size="small"
          type="primary"
          plain
          @click="
            () => {
              curCommodity = item
              buyCount = 1
            }
          "
          :style="{ borderColor: curCommodity?.id === item.id ? 'red' : '' }"
          >{{ curCommodityInfo(item.key)?.name }}{{ item.count ? `* ${item.count}` : '' }}</el-button
        >
      </div>
      <p>中价值</p>
      <div class="item" v-for="item of commodityList?.slice(8, 12)" :key="item.id">
        <el-button
          size="small"
          type="primary"
          plain
          @click="
            () => {
              curCommodity = item
              buyCount = 1
            }
          "
          :style="{ borderColor: curCommodity?.id === item.id ? 'red' : '' }"
          >{{ curCommodityInfo(item.key)?.name }}{{ item.count ? `* ${item.count}` : '' }}</el-button
        >
      </div>
      <p>低价值</p>
      <div class="item" v-for="item of commodityList?.slice(12)" :key="item.id">
        <el-button
          size="small"
          type="primary"
          plain
          @click="
            () => {
              curCommodity = item
              buyCount = 1
            }
          "
          :style="{ borderColor: curCommodity?.id === item.id ? 'red' : '' }"
          >{{ curCommodityInfo(item.key)?.name }}{{ item.count ? `* ${item.count}` : '' }}</el-button
        >
      </div>
    </div>
    <div class="commodity-detail border-wrap">
      <h4>商品详情</h4>
      <p>
        名称：{{ commodityInfoList.find((info) => info.key === curCommodity?.key)?.name || '未选择'
        }}{{ curCommodity?.count ? `* ${curCommodity.count}` : '' }}
      </p>
      <p>
        说明：<span>{{ curCommodityInfo(curCommodity?.key)?.desc || '' }}</span>
      </p>
      <p>
        购买数量：
        <el-input-number
          size="small"
          :min="1"
          :max="(curCommodity?.max || 10000) - (buyCountMap?.find(([id]) => curCommodity?.id === id)?.[1] || 0)"
          v-model="buyCount"
          :precision="0"
          :disabled="!curCommodity"
        ></el-input-number>
      </p>
      <p>
        <span>价格：{{ (curCommodity?.price || 0) * buyCount }}💲</span>
        <span style="margin-left: 15px"
          >限购：{{ buyCountMap?.find(([id]) => curCommodity?.id === id)?.[1] || 0 }} / {{ curCommodity?.max || '∞' }}</span
        >
      </p>
      <el-button size="small" type="primary" plain style="width: 100%" @click="handleBuyCommodity"> 购入 </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.commodity-list-wrap {
  margin-bottom: 10px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  height: 100%;
  .commodity-list {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    overflow: auto;
    min-height: 50px;
    font-size: 12px;
    p {
      width: 100%;
      flex-shrink: 0;
    }
    .item {
      width: calc(50% - 5px);
      margin-bottom: 10px;
      button {
        width: 100%;
      }
    }
  }
}
.commodity-detail {
  flex-shrink: 0;
  overflow: auto;
  h4 {
    margin-top: 0;
  }
  p {
    margin: 6px 0;
    font-size: 13px;
  }
}

:deep() {
  .el-button:focus,
  .el-button:hover {
    background-color: unset;
  }
}
.btn-wrap {
  & > div {
    justify-content: unset;
  }
  .common-btn-wrap {
    margin-bottom: 10px;
  }
}
.border-wrap {
  padding: 10px;
  p {
    font-size: 14px;
    margin: 5px 0;
  }
}
</style>
